import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { GeocodeCandidate } from "@shared/api";
import { ADDRESS_NOT_FOUND_CODE, api, ApiError } from "../../api/client";
import {
  defaultAddressStatus,
  geocodeAddress
} from "../addressSearchCore";
import type { ViewerTopNavModel } from "./ViewerTopNav";
import type { AddressEditorSkin } from "./addressEditorSkin";

const DEBOUNCE_MS = 220;

function SvgClear(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

export interface ViewerAddressEditorProps {
  model: ViewerTopNavModel;
  /** Pill label when idle (full address vs abbreviated street). */
  readonlyLabel: string;
  /** Tooltip on the idle pill (e.g. full address when label is abbreviated). */
  readonlyTooltip?: string;
  skin: AddressEditorSkin;
  /** Open geocode dropdown above the pill (mobile bottom chrome). */
  dropdownUp?: boolean;
  addressInputId?: string;
}

export function ViewerAddressEditor({
  model,
  readonlyLabel,
  readonlyTooltip,
  skin,
  dropdownUp = false,
  addressInputId
}: ViewerAddressEditorProps): ReactElement {
  const menuId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(model.address);
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);
  const [resolving, setResolving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!editing) {
      setDraft(model.address);
    }
  }, [model.address, editing]);

  const closeMenu = useCallback(() => {
    setCandidates([]);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
    reqRef.current += 1;
    setResolving(false);
    setEditing(false);
    setDraft(model.address);
    setStatus("");
    setStatusError(false);
    closeMenu();
  }, [location.key, model.address, closeMenu]);

  const runQuery = useCallback(async (query: string) => {
    const requestId = ++reqRef.current;
    const trimmed = query.trim();
    if (!trimmed) {
      setCandidates([]);
      setActiveIndex(-1);
      setStatus("");
      setStatusError(false);
      return;
    }
    setStatus("Searching…");
    setStatusError(false);
    const { candidates: list, errorMessage } = await geocodeAddress(trimmed);
    if (requestId !== reqRef.current) return;
    if (errorMessage) {
      setCandidates([]);
      setActiveIndex(-1);
      setStatus(errorMessage);
      setStatusError(true);
      return;
    }
    setCandidates(list);
    const nextActive = list.length > 0 ? 0 : -1;
    setActiveIndex(nextActive);
    const d = defaultAddressStatus(list.length, trimmed);
    setStatus(d.text);
    setStatusError(false);
  }, []);

  const onInputChange = useCallback(
    (value: string) => {
      setDraft(value);
      if (timerRef.current != null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void runQuery(value), DEBOUNCE_MS);
    },
    [runQuery]
  );

  const commit = useCallback(
    async (addr: string, coords: { lat: number; lon: number } | null = null) => {
      if (resolving) return;
      const params = new URLSearchParams();
      params.set("address", addr);
      if (coords) {
        params.set("lat", String(coords.lat));
        params.set("lon", String(coords.lon));
      }
      setResolving(true);
      setStatus("Scanning...");
      setStatusError(false);
      setCandidates([]);
      setActiveIndex(-1);
      try {
        // Use the Regrid-free preview endpoint. The viewer's address
        // re-search is a "shop another address" flow — Regrid is paid
        // for at fulfillment time via the modal pin-confirm + Stripe
        // path, not on every address change. This change also unifies
        // the UX with the marketing search.
        const site = coords
          ? await api.preview({
              lat: coords.lat,
              lon: coords.lon,
              address: addr
            })
          : await api.preview({ address: addr });
        const { preloadViewerTerrainForSite } = await import("../../main");
        const dem = await preloadViewerTerrainForSite(site);
        navigate(`/viewer?${params.toString()}`, {
          state: {
            preloadedSite: {
              response: site,
              queryAddress: addr,
              queryCoords: coords,
              dem
            }
          }
        });
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.code === ADDRESS_NOT_FOUND_CODE
              ? "We couldn't fully resolve that address. Try including the city and state."
              : err.message
            : (err as Error).message;
        setStatus(msg);
        setStatusError(true);
        setResolving(false);
      }
    },
    [navigate, resolving]
  );

  const onPickCandidate = useCallback(
    (c: GeocodeCandidate) => {
      void commit(c.formattedAddress, {
        lat: c.location.lat,
        lon: c.location.lon
      });
    },
    [commit]
  );

  const beginEdit = useCallback(() => {
    setEditing(true);
    setDraft(model.address);
    closeMenu();
    setStatus("");
    queueMicrotask(() => inputRef.current?.focus());
  }, [model.address, closeMenu]);

  const doneEdit = useCallback(() => {
    if (resolving) return;
    setEditing(false);
    setDraft(model.address);
    closeMenu();
    setStatus("");
    setStatusError(false);
  }, [model.address, closeMenu, resolving]);

  const onClear = useCallback(
    (ev: MouseEvent) => {
      ev.stopPropagation();
      if (editing && !resolving) {
        setDraft("");
        closeMenu();
        setStatus("");
        setStatusError(false);
        if (timerRef.current != null) clearTimeout(timerRef.current);
        void runQuery("");
        queueMicrotask(() => inputRef.current?.focus());
      }
    },
    [editing, resolving, closeMenu, runQuery]
  );

  const showClear =
    editing && draft.trim().length > 0 && !resolving;

  const showDropdown =
    editing && !resolving && (candidates.length > 0 || status.length > 0);

  const dropdownClass =
    dropdownUp && skin.dropdownPanelUp
      ? `${skin.dropdownPanel} ${skin.dropdownPanelUp}`
      : skin.dropdownPanel;

  return (
    <div className={skin.addressBlock}>
      <div
        className={
          resolving
            ? `${skin.addressPill} ${skin.addressPillScanning}`
            : skin.addressPill
        }
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? menuId : undefined}
        aria-haspopup="listbox"
        title={editing ? undefined : readonlyTooltip}
        onClick={() => {
          if (!editing) beginEdit();
        }}
      >
        {editing ? (
          <>
            <input
              ref={inputRef}
              id={addressInputId}
              className={skin.addressInput}
              type="text"
              enterKeyHint="search"
              value={draft}
              placeholder="Search any US address…"
              autoComplete="off"
              spellCheck={false}
              readOnly={resolving}
              aria-busy={resolving || undefined}
              aria-label="Search address"
              aria-controls={menuId}
              aria-autocomplete="list"
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(ev) => {
                if (resolving) {
                  ev.preventDefault();
                  return;
                }
                if (ev.key === "ArrowDown") {
                  ev.preventDefault();
                  if (candidates.length === 0) return;
                  setActiveIndex((i) =>
                    i < 0 ? 0 : (i + 1) % candidates.length
                  );
                } else if (ev.key === "ArrowUp") {
                  ev.preventDefault();
                  if (candidates.length === 0) return;
                  setActiveIndex((i) =>
                    i <= 0 ? candidates.length - 1 : i - 1
                  );
                } else if (ev.key === "Enter") {
                  ev.preventDefault();
                  const pick =
                    activeIndex >= 0
                      ? candidates[activeIndex]
                      : candidates[0];
                  if (pick) {
                    onPickCandidate(pick);
                  } else if (draft.trim()) {
                    void commit(draft.trim());
                  }
                } else if (ev.key === "Escape") {
                  ev.preventDefault();
                  closeMenu();
                  doneEdit();
                }
              }}
              onBlur={() => {
                window.setTimeout(() => {
                  if (resolving) return;
                  closeMenu();
                  doneEdit();
                }, 120);
              }}
            />
            {resolving ? (
              <span className={skin.scanningLabel}>Scanning...</span>
            ) : null}
            {showClear ? (
              <button
                type="button"
                className={skin.clearBtn}
                aria-label="Clear address"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClear}
              >
                <SvgClear />
              </button>
            ) : null}
          </>
        ) : (
          <span className={skin.addressReadonly}>{readonlyLabel}</span>
        )}
      </div>
      {showDropdown ? (
        <div
          id={menuId}
          className={dropdownClass}
          role={candidates.length > 0 ? "listbox" : undefined}
          aria-label={
            candidates.length === 0 && status
              ? "Address search status"
              : undefined
          }
        >
          {candidates.map((c, i) => (
            <button
              key={`${c.formattedAddress}-${i}`}
              type="button"
              role="option"
              aria-selected={i === activeIndex}
              className={
                i === activeIndex
                  ? `${skin.menuItem} ${skin.menuItemActive}`
                  : skin.menuItem
              }
              onMouseDown={(e) => {
                e.preventDefault();
                onPickCandidate(c);
              }}
            >
              {c.formattedAddress}
            </button>
          ))}
          {status ? (
            <div
              className={
                statusError
                  ? `${skin.dropdownStatus} ${skin.dropdownStatusError}`
                  : skin.dropdownStatus
              }
              role="status"
              aria-live="polite"
            >
              {status}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
